import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  TablePagination,
  IconButton,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";
import { useTheme } from "@mui/material/styles"; // ✅ Import useTheme

const getStatusChipProps = (status, theme) => {
  if (!theme?.palette) {
    return {
      label: status,
      variant: "outlined",
      size: "small",
      sx: {
        textTransform: "capitalize",
        fontWeight: 500,
      },
    };
  }

  const colorMap = {
    active: {
      color: theme.palette.success.main,
      borderColor: theme.palette.success.light,
    },
    completed: {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.light,
    },
    dropped: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.light,
    },
    on_hold: {
      color: theme.palette.warning.main,
      borderColor: theme.palette.warning.light,
    },
    pending: {
      color: theme.palette.warning.main,
      borderColor: theme.palette.warning.light,
    },
    rejected: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.light,
    },
    approved: {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.light,
    },
  };

  const selected = colorMap[status?.toLowerCase()] || {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
  };

  return {
    label: status,
    variant: "outlined",
    size: "small",
    sx: {
      textTransform: "capitalize",
      fontWeight: 500,
      color: selected.color,
      borderColor: selected.borderColor,
    },
  };
};

const DynamicTable = ({
  columns,
  rows,
  total,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  onSort,
  sortField,
  sortOrder,
  loading,
  processMode = false,
  onRowClick, // New optional prop
}) => {
  const theme = useTheme(); // Get theme once
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <TableContainer sx={{ borderRadius: 3 }}>
        <Table stickyHeader sx={{ minWidth: 300 }}>
          <TableHead
            sx={{
              textAlign: "center",
            }}
          >
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  className="divCell"
                  key={col.key}
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: 600,
                    textAlign: "center",
                    borderBottom: "2px solid",
                    fontSize: "0.95rem",
                    py: 0.8,
                    cursor: col.sortable ? "pointer" : "default",
                  }}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  <Box display="flex" alignItems="center" textAlign="center">
                    {col.label}
                    {col.sortable && sortField === col.key && (
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        {sortOrder === "asc" ? (
                          <ArrowUpward fontSize="inherit" />
                        ) : (
                          <ArrowDownward fontSize="inherit" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((col) => {
                    const rawValue = col.key
                      .split(".")
                      .reduce((acc, key) => acc?.[key], row);

                    const value =
                      col.key === "studentDetails.status" ||
                      col.key === "userStatus" ? (
                        <Chip {...getStatusChipProps(rawValue, theme)} />
                      ) : col.render ? (
                        col.render(row)
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {rawValue ?? "—"}
                        </Typography>
                      );

                    return (
                      <TableCell key={col.key} sx={{ py: 0.8 }}>
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box mt={2} display="flex" justifyContent="flex-end">
        <TablePagination
          component="div"
          count={total}
          page={page - 1} // MUI expects zero-based index
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            onPerPageChange(parseInt(e.target.value, 10));
            onPageChange(1); // reset to first page
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default DynamicTable;
